/**
 * 머리글 정렬(`DataTable` · `Column.sort`)에 쓰는 **등급 차례** 한 곳.
 *
 * 🔴 **글자 순이 아니라 등급 순이다.** 심각도를 글자로 세우면 `CRITICAL < ERROR < INFO < WARN` 이 되어
 *    내림차순 맨 위가 「주의」다. 표마다 따로 적으면 한쪽만 고쳐진다 — 여기서 나눠 쓴다.
 * 모든 차례는 **오름차순 기준**(낮은 것 → 높은 것)이다. 머리글을 처음 누르면 내림차순이라 가장 급한 것이 위에 선다.
 */
import type { LogEntry } from '@ctcells/contract'

/** 알람·이벤트 심각도(`INFO · WARN · ERROR · CRITICAL`). 모르는 값은 가장 낮게 본다 */
const SEVERITY_RANK: Readonly<Record<string, number>> = { INFO: 0, WARN: 1, ERROR: 2, CRITICAL: 3 }

export function severityRank(value: string | null | undefined): number {
  return value == null ? -1 : (SEVERITY_RANK[value] ?? -1)
}

/** 시스템 로그 수준(`debug · info · warn · error · critical`) */
const LOG_LEVEL_RANK: Readonly<Record<LogEntry['level'], number>> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  critical: 4,
}

export function logLevelRank(value: LogEntry['level']): number {
  return LOG_LEVEL_RANK[value]
}

/**
 * 시료 상태(처분) — 진행 중 → 완료 → 폐기. 표기 순서와 같다(시료 목록 탭 줄과도 같다).
 * 시료 목록 · 작업 상세 시료 표가 같이 쓴다. 모르는 값은 가장 뒤(높게)다.
 */
const SAMPLE_STATUS_RANK: Readonly<Record<string, number>> = {
  in_process: 0,
  completed: 1,
  discarded: 2,
}

export function sampleStatusRank(value: string | null | undefined): number {
  return value == null ? 9 : (SAMPLE_STATUS_RANK[value] ?? 9)
}

/**
 * 작업 상태 — 삶의 차례(생성 → 대기 → 실행 → 일시정지 → 오류 → 완료 → 중지). 🔴 글자 순이면 `aborted` 가
 * 맨 앞이다. 머리글을 처음 누르면 내림차순이라 진행 중 목록에서는 **오류(복구 대기)** 가, 이력에서는 **중지**가 위에 선다.
 */
const BATCH_STATUS_RANK: Readonly<Record<string, number>> = {
  created: 0,
  queued: 1,
  running: 2,
  paused: 3,
  error: 4,
  completed: 5,
  aborted: 6,
}

export function batchStatusRank(value: string): number {
  return BATCH_STATUS_RANK[value] ?? -1
}

/**
 * 웰의 **트레이 차례** — 첫 웰이 트레이 칸 목록(`TRAY_SLOTS`)의 몇 번째인가.
 * 🔴 글자 순이면 `A10` 이 `A2` 앞이고, 트레이는 행 우선이 아닐 수 있다 — 칸 목록이 곧 차례다.
 * 트레이 밖 웰은 칸 뒤(`slots.length`), **웰이 없으면 `null`**(= 정렬에서 방향 무관 맨 뒤 · `tableSort.ts`).
 * 작업 상세 시료 표의 처음 차례(`sampleRowsOf`)와 「웰」 머리글 정렬이 같이 쓴다.
 */
export function trayWellRank(wells: readonly string[], slots: readonly string[]): number | null {
  const first = wells[0]
  if (first === undefined) return null
  const at = slots.indexOf(first)
  return at === -1 ? slots.length : at
}
